<template>
  <a-select
    :mode="computedMode"
    show-search
    option-label-prop="label"
    :value="value"
    :placeholder="placeholder"
    :disabled="disabled"
    :filter-option="false"
    :not-found-content="cardSearchLoading ? null : cardSearchNotFoundContent"
    :loading="cardSearchLoading"
    style="width: 100%"
    @search="onSearchInput"
    @select="handleSelect"
    @deselect="handleDeselect"
  >
    <template v-if="cardSearchLoading" #notFoundContent>
      <a-spin size="small" />
    </template>
    <a-select-option
      v-for="item in optionsToRender"
      :key="item.randomNo"
      :value="item.randomNo"
      :label="cardOptionSelectedLabel(item)"
      :disabled="isOptionDisabled(item)"
    >
      <!-- 검색된 카드 1건 — 1행: No.+제품명, 2행: GTIN+배치번호+매칭배지/상태배지
           (verify 화면과 동일 용어: 난수/GTIN/배치번호/제품명) -->
      <div class="card-search-option">
        <div class="card-search-option-row1">
          <span class="card-search-no">No.{{ item.randomNo }}</span>
          <span class="card-search-product">{{ item.productName || "-" }}</span>
        </div>
        <div class="card-search-option-row2">
          <span class="card-search-gtin">{{ $t("certCard_search_col_gtin") }} {{ item.gtin || "-" }}</span>
          <span class="card-search-lot">{{ $t("certCard_search_col_lotBatch") }} {{ item.inboundBatchNumber || "-" }}</span>
          <span v-if="item.matchedField" class="card-search-match-badge">
            {{ matchedFieldLabel(item.matchedField) }}
          </span>
          <span v-if="item.used" class="card-search-used-reason">
            {{ item.usedReason || $t("workorder_form_card_used") }}
          </span>
          <span
            v-else-if="badgeFor(item)"
            :class="['card-search-status-badge', `card-search-status-badge--${badgeFor(item).tone}`]"
          >
            {{ badgeFor(item).text }}
          </span>
        </div>
      </div>
    </a-select-option>
  </a-select>
</template>

<script>
// 실물인증서 카드 검색 공유 UI 컴포넌트 — a-select show-search + 2행 옵션 렌더링 +
// useCertCardSearch(요청/정규화/디바운스/race-guard) 배선을 캡슐화한다.
// SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 원 계약(useCertCardSearch.js 헤더) 그대로:
// 이 컴포넌트도 "요청 실행 + 결과 렌더링"만 책임진다. 필드 조합·화면별 disabled/badge 정책은
// optionDisabled/optionBadge prop으로 각 소비 화면(Step3/작업지시서 수정모달)이 주입한다.
import { createCertCardSearch } from "@/composables/useCertCardSearch";

export default {
  name: "CertCardSearchSelect",
  props: {
    mode: {
      type: String,
      default: "single",
      validator: (v) => ["single", "multiple"].includes(v),
    },
    // single: String(randomNo) | multiple: String[](randomNo[])
    value: {
      type: [String, Array],
      default: undefined,
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 20,
    },
    // (item) => boolean — 화면별 옵션 비활성 정책(예: 인-오더 다른 치아가 이미 선택함).
    // 기본값은 카드 자체의 used 플래그만 반영한다(공통 데이터 모델).
    optionDisabled: {
      type: Function,
      default: (item) => item.used === true,
    },
    // (item) => { text: string, tone: 'danger'|'success'|'info' } | null — used가 아닐 때 표시할
    // 화면별 추가 배지(예: "이 그룹에 담김 ✓"). used 배지 자체는 이 컴포넌트가 공통으로 처리한다.
    optionBadge: {
      type: Function,
      default: () => null,
    },
    // single 모드 전용 — 모달 오픈 시점에 이미 배정된 randomNo의 표시 라벨을 미리 채우기 위한 시드 항목.
    // (검색 전에는 a-select가 해당 randomNo의 옵션을 렌더링한 적이 없어 라벨이 비어 보이는 문제 방지)
    initialOption: {
      type: Object,
      default: null,
    },
  },
  emits: ["select", "deselect", "update:value"],
  data() {
    return {
      cardSearchResults: [],
      cardSearchLoading: false,
      cardSearchTerm: "",
      // single 모드에서 현재 선택된 카드의 전체 데이터를 유지 — 검색 결과가 바뀌어도
      // 이미 선택된 항목의 라벨이 사라지지 않도록 한다.
      pinnedSelection: null,
    };
  },
  computed: {
    computedMode() {
      return this.mode === "multiple" ? "multiple" : undefined;
    },
    cardSearchNotFoundContent() {
      const term = (this.cardSearchTerm || "").trim();
      if (term.length < 2) {
        return this.$t("workorder_form_card_search_hint");
      }
      return this.$t("workorder_form_card_search_empty");
    },
    // 검색 결과 + (single 모드) 현재 선택된 카드의 pinned 항목을 병합한 렌더 대상 목록.
    optionsToRender() {
      const opts = [...this.cardSearchResults];
      if (this.mode !== "multiple" && this.pinnedSelection) {
        const exists = opts.some((o) => o.randomNo === this.pinnedSelection.randomNo);
        if (!exists) opts.unshift(this.pinnedSelection);
      }
      return opts;
    },
  },
  watch: {
    value(newVal) {
      if (this.mode === "multiple") return;
      if (!newVal) {
        this.pinnedSelection = null;
      }
    },
  },
  created() {
    // 실물카드 검색 공유 코어 — 요청/정규화/디바운스(300ms)/race-guard만 담당
    this.certCardSearch = createCertCardSearch();
    if (
      this.mode !== "multiple" &&
      this.value &&
      this.initialOption &&
      this.initialOption.randomNo === this.value
    ) {
      this.pinnedSelection = this.initialOption;
    }
  },
  beforeUnmount() {
    if (this.certCardSearch) {
      this.certCardSearch.dispose();
    }
  },
  methods: {
    onSearchInput(term) {
      // 정품조회 화면에서 복사한 "No.2003487255" 형태를 그대로 붙여넣어도 검색되도록
      // 선행 "No." 토큰만 제거한다(UX 개선, 09-14).
      const normalized = this.normalizeCardSearchTerm(term);
      this.cardSearchTerm = normalized;
      this.certCardSearch.search(
        normalized,
        { limit: this.limit },
        {
          onLoading: (loading) => {
            this.cardSearchLoading = loading;
          },
          onResult: (items) => {
            this.cardSearchResults = items;
          },
          onError: () => {
            this.cardSearchResults = [];
          },
        }
      );
    },

    // 검색어 선행 "No."/"No"/"no." 토큰만 제거 (숫자 직전에서만 매치 — 상품명 등 오검색 방지)
    normalizeCardSearchTerm(term) {
      if (!term) return term;
      return term.replace(/^\s*no\.?\s*(?=\d)/i, "");
    },

    // a-select 닫힌 상태에 표시되는 선택값 라벨(option-label-prop) — 콤팩트 1행 텍스트
    cardOptionSelectedLabel(item) {
      const productPart = item.productName ? ` ${item.productName}` : "";
      return `No.${item.randomNo}${productPart}`;
    },

    // 매칭 필드 배지 라벨 — "매칭:" 어투는 유지하되, 필드 용어는 정품조회 화면과 동일하게(난수/GTIN/배치번호)
    matchedFieldLabel(field) {
      const labels = {
        randomNo: this.$t("workorder_form_card_match_randomNo"),
        gtin: this.$t("workorder_form_card_match_gtin"),
        inboundBatchNumber: this.$t("workorder_form_card_match_lot"),
      };
      return labels[field] || field;
    },

    isOptionDisabled(item) {
      return !!this.optionDisabled(item);
    },

    badgeFor(item) {
      return this.optionBadge(item) || null;
    },

    findItem(randomNo) {
      return (
        this.optionsToRender.find((o) => o.randomNo === randomNo) ||
        (this.pinnedSelection && this.pinnedSelection.randomNo === randomNo ? this.pinnedSelection : null)
      );
    },

    handleSelect(randomNo) {
      const item = this.findItem(randomNo);
      if (!item) return;
      if (this.mode !== "multiple") {
        this.pinnedSelection = item;
        this.$emit("update:value", randomNo);
      }
      this.$emit("select", item);
    },

    handleDeselect(randomNo) {
      const item = this.findItem(randomNo);
      if (this.mode !== "multiple") {
        this.pinnedSelection = null;
        this.$emit("update:value", undefined);
      }
      this.$emit("deselect", item || { randomNo });
    },
  },
};
</script>

<!-- 실물카드 검색 드롭다운 옵션 스타일 (unscoped - a-select 드롭다운은 body에 렌더링됨)
     SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 M2 origin, CertCardSearchSelect 추출로 공용화 -->
<style lang="scss">
.card-search-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0;
  white-space: normal;

  .card-search-option-row1 {
    display: flex;
    align-items: baseline;
    gap: 8px;

    .card-search-no {
      font-weight: 600;
      font-family: "SF Mono", Monaco, "Consolas", monospace;
      color: #2f3337;
      font-size: 13px;
    }

    .card-search-product {
      color: #4b5563;
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .card-search-option-row2 {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .card-search-gtin,
    .card-search-lot {
      color: #9ca3af;
      font-size: 12px;
    }

    .card-search-match-badge {
      padding: 0 6px;
      border-radius: 4px;
      background-color: #eef2ff;
      color: #4338ca;
      font-size: 11px;
      line-height: 18px;
    }

    .card-search-used-reason {
      color: #dc2626;
      font-size: 12px;
    }

    // 화면별 추가 상태 배지 (optionBadge prop) — tone별 색상
    .card-search-status-badge {
      padding: 0 6px;
      border-radius: 4px;
      font-size: 11px;
      line-height: 18px;

      &--danger {
        background-color: #fef2f2;
        color: #dc2626;
      }

      &--success {
        background-color: #ecfdf5;
        color: #059669;
      }

      &--info {
        background-color: #eef2ff;
        color: #4338ca;
      }
    }
  }
}
</style>
